import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  listGroups,
  createGroup,
  attestStream,
  type EnvironmentCert,
  type Stream
} from '@/api/fleet';
import { STREAM_META } from '@/lib/format';

const ENVS: { value: EnvironmentCert; label: string; sub: string }[] = [
  {
    value: 'bare_metal',
    label: 'Bare metal',
    sub: 'Dedicated server in your DC or colo'
  },
  {
    value: 'colo',
    label: 'Colocation',
    sub: 'Your hardware in a shared DC'
  },
  {
    value: 'residential',
    label: 'Residential',
    sub: 'Home / SOHO ISP connection'
  },
  { value: 'mobile', label: 'Mobile', sub: 'Cellular / 4G/5G uplink' }
];

const ALL_STREAMS: Stream[] = [
  'cpu_compute',
  'gpu_compute',
  'storage',
  'bandwidth'
];

export function GroupsScreen() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['fleet-groups'],
    queryFn: listGroups
  });
  const groups = data?.groups ?? [];

  const attest = useMutation({
    mutationFn: ({ groupId, stream }: { groupId: string; stream: Stream }) =>
      attestStream(groupId, stream),
    onSuccess: () => {
      toast.success('Stream attestation recorded');
      qc.invalidateQueries({ queryKey: ['fleet-groups'] });
    },
    onError: (e: Error) => toast.error(e.message)
  });

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Groups</h1>
          <p className="mt-1 max-w-2xl text-sm text-nurev-muted">
            Group your nodes by environment certification. The environment cert
            gates which revenue streams nodes in the group are eligible for —
            e.g. residential ISP nodes can't run mining workloads.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> New group
        </button>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading groups…
        </div>
      ) : groups.length === 0 ? (
        <div className="card text-center text-sm text-nurev-muted">
          No groups yet. Create one to start organizing your nodes.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {groups.map(g => (
            <div key={g._id} className="card">
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-nurev-text">
                    {g.groupName}
                  </h3>
                  <p className="text-xs text-nurev-muted">
                    {ENVS.find(e => e.value === g.environmentCert)?.label ||
                      g.environmentCert}
                    {g.region ? ` · ${g.region}` : ''}
                    {g.nodeCount != null ? ` · ${g.nodeCount} nodes` : ''}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                  Allowed streams + attestation status
                </div>
                {ALL_STREAMS.map(s => {
                  const meta = STREAM_META[s];
                  const allowed = g.allowedStreams.includes(s);
                  const attested = g.attestations.some(a => a.stream === s);
                  return (
                    <div
                      key={s}
                      className="flex items-center justify-between rounded-lg border border-nurev-border bg-white/[0.02] px-3 py-2 text-xs"
                    >
                      <span className={allowed ? 'text-nurev-text' : 'text-nurev-muted line-through'}>
                        {meta.emoji} {meta.label}
                      </span>
                      {allowed && (
                        attested ? (
                          <span className="inline-flex items-center gap-1 text-emerald-300">
                            <ShieldCheck className="h-3 w-3" /> Attested
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              attest.mutate({ groupId: g._id, stream: s })
                            }
                            disabled={attest.isPending}
                            className="text-amber-300 hover:underline"
                          >
                            Attest →
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <CreateGroupModal
          onClose={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['fleet-groups'] });
          }}
        />
      )}
    </div>
  );
}

function CreateGroupModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [groupName, setGroupName] = useState('');
  const [environmentCert, setEnvironmentCert] = useState<EnvironmentCert>('bare_metal');
  const [region, setRegion] = useState('');
  const [streams, setStreams] = useState<Stream[]>(['cpu_compute', 'bandwidth']);

  const mut = useMutation({
    mutationFn: () =>
      createGroup({ groupName, environmentCert, region, allowedStreams: streams }),
    onSuccess
  });

  const toggle = (s: Stream) =>
    setStreams(prev => (prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!groupName) return;
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg space-y-4 rounded-2xl border border-emerald-500/30 bg-nurev-surface p-6"
      >
        <h2 className="text-lg font-bold text-nurev-text">New group</h2>

        <div>
          <label className="label">Group name</label>
          <input
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            placeholder="us-east-bm"
            className="input"
            required
          />
        </div>

        <div>
          <label className="label">Environment certification</label>
          <div className="grid grid-cols-2 gap-2">
            {ENVS.map(env => (
              <button
                key={env.value}
                type="button"
                onClick={() => setEnvironmentCert(env.value)}
                className={`rounded-lg border p-3 text-left text-xs transition ${
                  environmentCert === env.value
                    ? 'border-emerald-500/50 bg-emerald-500/10'
                    : 'border-nurev-border hover:border-emerald-500/30'
                }`}
              >
                <div className="font-bold text-nurev-text">{env.label}</div>
                <div className="text-nurev-muted">{env.sub}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Region (optional)</label>
          <input
            value={region}
            onChange={e => setRegion(e.target.value)}
            placeholder="us-east-1"
            className="input"
          />
        </div>

        <div>
          <label className="label">Allowed streams</label>
          <div className="space-y-1.5">
            {ALL_STREAMS.map(s => {
              const meta = STREAM_META[s];
              return (
                <label
                  key={s}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-nurev-border bg-white/[0.02] px-3 py-2 text-sm hover:border-emerald-500/30"
                >
                  <input
                    type="checkbox"
                    checked={streams.includes(s)}
                    onChange={() => toggle(s)}
                  />
                  <span className="text-nurev-text">
                    {meta.emoji} {meta.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {mut.error && (
          <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {(mut.error as Error).message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={mut.isPending} className="btn-primary">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create group'}
          </button>
        </div>
      </form>
    </div>
  );
}
